import React, { useState, useEffect, useCallback } from 'react';
import { rbacAnalyticsAPI, roleAPI, userRoleAPI, permissionAPI } from '../../services/rbacApi';
import LoadingSpinner from '../Common/LoadingSpinner';
import CustomSelect from '../Common/CustomSelect';
import { 
    ArrowPathIcon, 
    ArrowDownTrayIcon, 
    XMarkIcon,
    ClipboardIcon,
    CheckIcon,
    ComputerDesktopIcon,
    MapPinIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AccessHistoryViewer = () => {
    // State management
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Filters
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Summary stats
    const [summary, setSummary] = useState({
        total: 0,
        loginsSuccess: 0,
        loginsFailed: 0,
        uniqueUsersCount: 0
    });

    // Details sliding drawer state
    const [selectedLog, setSelectedLog] = useState(null);
    const [copied, setCopied] = useState(false);
    const [auditPermissions, setAuditPermissions] = useState([]);
    const [auditPermissionsLoading, setAuditPermissionsLoading] = useState(false);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Fetch permissions when selectedLog changes
    useEffect(() => {
        if (!selectedLog) {
            setAuditPermissions([]);
            return;
        }

        const loadPermissions = async () => {
            setAuditPermissionsLoading(true);
            setAuditPermissions([]);
            try {
                // Scenario 1: RBAC Role Related Actions - Load permissions of the role being modified
                const roleActions = ['ROLE_ASSIGNED', 'ROLE_CREATED', 'ROLE_UPDATED', 'PERMISSIONS_UPDATED'];
                if (roleActions.includes(selectedLog.action) && selectedLog.details?.roleCode) {
                    if (selectedLog.action === 'PERMISSIONS_UPDATED' && selectedLog.details?.permissions && Array.isArray(selectedLog.details.permissions)) {
                        const permissionsList = selectedLog.details.permissions;
                        const updateType = selectedLog.details.updateType || 'assign';
                        
                        // Check if the permissions in log are already detailed objects
                        const isDetailed = permissionsList.length > 0 && typeof permissionsList[0] === 'object';
                        
                        if (isDetailed) {
                            // Map directly from the saved details in the database log record
                            const detailedPerms = permissionsList.map(p => ({
                                code: p.code,
                                name: p.name || p.code.replace(/_/g, ' '),
                                module: p.module || p.code.split(':')[0] || 'SYSTEM',
                                description: p.description || 'System Permission',
                                changeType: updateType === 'revoke' ? 'revoked' : 'granted'
                            }));
                            setAuditPermissions(detailedPerms);
                        } else {
                            // Legacy string array compatibility — resolve codes via catalog (1 request)
                            const permCodes = permissionsList;
                            const allPerms = await permissionAPI.fetchAllPermissions({ isActive: true });
                            const detailedPerms = allPerms
                                .filter(p => permCodes.includes(p.code))
                                .map(p => ({
                                    ...p,
                                    changeType: updateType === 'revoke' ? 'revoked' : 'granted'
                                }));
                                
                            const foundCodes = detailedPerms.map(p => p.code);
                            permCodes.forEach(code => {
                                if (!foundCodes.includes(code)) {
                                    const parts = code.split(':');
                                    detailedPerms.push({
                                        code,
                                        name: code.replace(/_/g, ' '),
                                        module: parts[0] || 'SYSTEM',
                                        description: 'System Permission',
                                        changeType: updateType === 'revoke' ? 'revoked' : 'granted'
                                    });
                                }
                            });
                            setAuditPermissions(detailedPerms);
                        }
                    } else {
                        const res = await roleAPI.getRole(selectedLog.details.roleCode);
                        if (res.data?.success) {
                            const roleData = res.data.data?.role || {};
                            const perms = roleData.permissions || [];
                            setAuditPermissions(perms);
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to load audit permissions:', err);
            } finally {
                setAuditPermissionsLoading(false);
            }
        };

        loadPermissions();
    }, [selectedLog]);

    // Fetch Access Logs from API
    const fetchAccessLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page,
                limit,
                search: search || undefined,
                action: actionFilter || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined
            };

            const response = await rbacAnalyticsAPI.getAccessLogs(params);
            
            if (response.data?.success) {
                setLogs(response.data.data.logs || []);
                setTotalPages(response.data.data.pagination?.totalPages || 1);
                setTotalCount(response.data.data.pagination?.totalCount || 0);
                if (response.data.data.summary) {
                    setSummary(response.data.data.summary);
                }
            } else {
                throw new Error(response.data?.message || 'Failed to fetch access history');
            }
        } catch (err) {
            console.error('Error fetching access history logs:', err);
            setError(err.message || 'Something went wrong while fetching access history');
        } finally {
            setLoading(false);
        }
    }, [page, limit, search, actionFilter, startDate, endDate]);

    // Fetch logs on dependencies change
    useEffect(() => {
        fetchAccessLogs();
    }, [fetchAccessLogs]);

    // Handle Reset Filters
    const handleResetFilters = () => {
        setSearchInput('');
        setSearch('');
        setActionFilter('');
        setStartDate('');
        setEndDate('');
        setPage(1);
        toast.success('Filters cleared successfully');
    };

    // Apply Quick Preset Filters
    const applyPreset = (preset) => {
        const today = new Date();
        setPage(1);
        
        switch (preset) {
            case '24h': {
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                setStartDate(yesterday.toISOString().slice(0, 10));
                setEndDate(today.toISOString().slice(0, 10));
                toast.success('Applied: Last 24 Hours');
                break;
            }
            case '7d': {
                const lastWeek = new Date(today);
                lastWeek.setDate(today.getDate() - 7);
                setStartDate(lastWeek.toISOString().slice(0, 10));
                setEndDate(today.toISOString().slice(0, 10));
                toast.success('Applied: Last 7 Days');
                break;
            }
            case 'logins': {
                setActionFilter('LOGIN_SUCCESS');
                toast.success('Applied: Successful Logins');
                break;
            }
            case 'rbac': {
                setActionFilter('RBAC_ALL');
                toast.success('Applied: All RBAC Updates');
                break;
            }
            default:
                break;
        }
    };

    // Export current page logs to CSV file
    const handleExportCSV = () => {
        if (!logs || logs.length === 0) {
            toast.error('No logs available to export');
            return;
        }

        const headers = ['Timestamp', 'User Name', 'User Email', 'Action', 'Description', 'IP Address', 'HTTP Method', 'Resource Path', 'Status Code', 'User Agent'];
        const rows = logs.map(log => [
            new Date(log.createdAt).toISOString(),
            `"${(log.userId?.name || 'System').replace(/"/g, '""')}"`,
            log.userEmail || 'N/A',
            log.action,
            `"${(log.description || '').replace(/"/g, '""')}"`,
            log.ipAddress || 'Unknown',
            log.method || 'N/A',
            log.resource || 'N/A',
            log.statusCode || 'N/A',
            `"${(log.userAgent || '').replace(/"/g, '""')}"`
        ]);

        const csvContent = "data:text/csv;charset=utf-8," 
            + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
            
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `access_history_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Access history exported successfully');
    };

    // Format Timestamp helper
    const formatTimestamp = (dateStr) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '—';
        
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        
        return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    };

    // Copy JSON payload to clipboard helper
    const handleCopyToClipboard = (obj) => {
        if (!obj) return;
        navigator.clipboard.writeText(JSON.stringify(obj, null, 2))
            .then(() => {
                setCopied(true);
                toast.success('Payload copied to clipboard!');
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                toast.error('Failed to copy details');
            });
    };

    // Simple user agent parser helper
    const parseUserAgent = (ua) => {
        if (!ua) return { browser: 'Unknown Browser', os: 'Unknown OS' };
        
        let browser = 'Other Browser';
        let os = 'Other OS';

        if (ua.includes('Firefox')) browser = 'Firefox';
        else if (ua.includes('Chrome') && !ua.includes('Chromium') && !ua.includes('Edg')) browser = 'Chrome';
        else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
        else if (ua.includes('Edg')) browser = 'Edge';
        else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';

        if (ua.includes('Windows')) os = 'Windows OS';
        else if (ua.includes('Macintosh') || ua.includes('Mac OS')) os = 'macOS';
        else if (ua.includes('Linux')) os = 'Linux OS';
        else if (ua.includes('Android')) os = 'Android OS';
        else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS Device';

        return { browser, os };
    };

    // Action style maps
    const getActionBadgeStyle = (action) => {
        switch (action) {
            case 'LOGIN_SUCCESS':
                return 'bg-green-50 text-green-700 border border-green-100';
            case 'LOGIN_FAILED':
                return 'bg-rose-50 text-rose-700 border border-rose-100';
            case 'LOGOUT':
                return 'bg-blue-50 text-blue-700 border border-blue-100';
            case 'ROLE_ASSIGNED':
            case 'ROLE_CREATED':
                return 'bg-purple-50 text-purple-700 border border-purple-100';
            case 'ROLE_REVOKED':
            case 'ROLE_DELETED':
                return 'bg-amber-50 text-amber-700 border border-amber-100';
            case 'USER_CREATED':
                return 'bg-indigo-50 text-indigo-700 border border-indigo-100';
            case 'USER_DELETED':
                return 'bg-red-50 text-red-700 border border-red-100';
            case 'PERMISSIONS_UPDATED':
                return 'bg-teal-50 text-teal-700 border border-teal-100';
            default:
                return 'bg-gray-50 text-gray-700 border border-gray-100';
        }
    };

    // Helper to get initials for avatar
    const getInitials = (name) => {
        if (!name) return 'U';
        return name
            .split(' ')
            .map(part => part[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();
    };

    // Action filter options
    const actionOptions = [
        { value: '', label: 'All Actions' },
        { value: 'RBAC_ALL', label: 'All RBAC Updates' },
        { value: 'LOGIN_SUCCESS', label: 'Login Success' },
        { value: 'LOGIN_FAILED', label: 'Login Failed' },
        { value: 'LOGOUT', label: 'Logout' },
        { value: 'ROLE_ASSIGNED', label: 'Role Assigned' },
        { value: 'ROLE_REVOKED', label: 'Role Revoked' },
        { value: 'USER_CREATED', label: 'User Created' },
        { value: 'USER_UPDATED', label: 'User Updated' },
        { value: 'USER_DELETED', label: 'User Deleted' },
        { value: 'ROLE_CREATED', label: 'Role Created' },
        { value: 'ROLE_UPDATED', label: 'Role Updated' },
        { value: 'ROLE_DELETED', label: 'Role Deleted' },
        { value: 'PERMISSIONS_UPDATED', label: 'Permissions Updated' }
    ];

    return (
        <div>
            <div className="max-w-7xl mx-auto bg-gray-50">
                {/* Main Content Card */}
                <div className="bg-white shadow-sm border border-gray-200">
                    
                    {/* Filters and Search - Aligned with UserPermissionsReport */}
                    <div className="px-4 sm:px-6 py-2 border-b border-gray-200 flex-shrink-0">
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Search */}
                            <div className="relative flex-1 min-w-[200px] max-w-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search by operator, email or description..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    className="block w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-md bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                                />
                            </div>

                            {/* Action selector */}
                            <div className="w-44">
                                <CustomSelect
                                    id="action-filter"
                                    value={actionFilter}
                                    onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
                                    options={actionOptions}
                                    size="sm"
                                    variant="minimal"
                                />
                            </div>

                            {/* Preset Buttons */}
                            <div className="flex flex-wrap items-center gap-1">
                                <button
                                    onClick={() => applyPreset('24h')}
                                    className="px-2 py-1 text-[10px] font-semibold rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-all shadow-xs"
                                >
                                    24h
                                </button>
                                <button
                                    onClick={() => applyPreset('7d')}
                                    className="px-2 py-1 text-[10px] font-semibold rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-all shadow-xs"
                                >
                                    7 Days
                                </button>
                                <button
                                    onClick={() => applyPreset('logins')}
                                    className="px-2 py-1 text-[10px] font-semibold rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-all shadow-xs"
                                >
                                    Logins
                                </button>
                                <button
                                    onClick={() => applyPreset('rbac')}
                                    className="px-2 py-1 text-[10px] font-semibold rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-all shadow-xs"
                                >
                                    RBAC
                                </button>
                            </div>

                            {/* Summary Stats & Download on Right */}
                            <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                                {/* Total */}
                                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 border border-gray-200">
                                    <span className="text-[10px] font-medium text-gray-500">Total</span>
                                    <span className="text-xs font-bold text-gray-800">{summary.total}</span>
                                </div>
                                {/* Login Acc count */}
                                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-50 border border-green-200">
                                    <span className="text-[10px] font-medium text-green-700">Success</span>
                                    <span className="text-xs font-bold text-green-800">{summary.loginsSuccess}</span>
                                </div>
                                {/* Failed Acc count */}
                                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 border border-red-200">
                                    <span className="text-[10px] font-medium text-red-700">Failed</span>
                                    <span className="text-xs font-bold text-red-800">{summary.loginsFailed}</span>
                                </div>

                                {/* Download Report Button */}
                                <button
                                    onClick={handleExportCSV}
                                    disabled={loading || logs.length === 0}
                                    className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-[10px] font-semibold shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    title="Download access logs as CSV"
                                >
                                    <ArrowDownTrayIcon className="w-3 h-3" />
                                    Download CSV
                                </button>
                            </div>
                        </div>

                        {/* Date pickers row */}
                        <div className="flex flex-wrap items-center gap-4 mt-2 pt-2 border-t border-gray-100 text-xs">
                            <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-gray-500 uppercase tracking-wider">Start Date:</span>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                                    className="px-2 py-1 border border-gray-300 rounded-md bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-indigo-500 transition-all"
                                />
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-gray-500 uppercase tracking-wider">End Date:</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                                    className="px-2 py-1 border border-gray-300 rounded-md bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-indigo-500 transition-all"
                                />
                            </div>

                            <button
                                onClick={handleResetFilters}
                                className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold text-gray-500 hover:text-indigo-600 hover:bg-gray-100 transition-all ml-auto"
                            >
                                <ArrowPathIcon className="h-3.5 w-3.5" />
                                Reset Filters
                            </button>
                        </div>
                    </div>

                    {/* Modern Pagination Section - Aligned with UserPermissionsReport */}
                    {!loading && !error && logs.length > 0 && (
                        <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                            </svg>
                                        </div>
                                        <div className="text-sm flex items-center gap-2">
                                            <div>
                                                <span className="font-semibold text-gray-900">
                                                    {((page - 1) * limit) + 1}-{Math.min(page * limit, totalCount)}
                                                </span>
                                                <span className="text-gray-500 mx-1">of</span>
                                                <span className="font-semibold text-gray-900">{totalCount}</span>
                                                <span className="text-gray-500 ml-1">activities</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Navigation Controls */}
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600 font-medium">Show:</span>
                                        <CustomSelect
                                            id="limit-top"
                                            value={limit}
                                            onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}
                                            options={[
                                                { value: 10,  label: '10'  },
                                                { value: 20,  label: '20'  },
                                                { value: 50,  label: '50'  },
                                                { value: 100, label: '100' },
                                            ]}
                                            size="sm"
                                            variant="minimal"
                                            className="min-w-[70px]"
                                        />
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setPage(1)}
                                            disabled={page <= 1}
                                            className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                            title="First page"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                            </svg>
                                        </button>

                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page <= 1}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                            <span className="hidden sm:inline">Previous</span>
                                        </button>

                                        {/* Page Numbers */}
                                        <div className="hidden md:flex items-center gap-1">
                                            {(() => {
                                                const pages = [];
                                                if (totalPages > 0) {
                                                    pages.push(
                                                        <button
                                                            key={1}
                                                            onClick={() => setPage(1)}
                                                            className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                                                page === 1
                                                                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            1
                                                        </button>
                                                    );
                                                }

                                                if (page > 3 && totalPages > 5) {
                                                    pages.push(
                                                        <span key="ellipsis-1" className="px-2 text-gray-400">
                                                            ...
                                                        </span>
                                                    );
                                                }

                                                const startPage = Math.max(2, page - 1);
                                                const endPage = Math.min(totalPages - 1, page + 1);

                                                for (let i = startPage; i <= endPage; i++) {
                                                    pages.push(
                                                        <button
                                                            key={i}
                                                            onClick={() => setPage(i)}
                                                            className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                                                page === i
                                                                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            {i}
                                                        </button>
                                                    );
                                                }

                                                if (page < totalPages - 2 && totalPages > 5) {
                                                    pages.push(
                                                        <span key="ellipsis-2" className="px-2 text-gray-400">
                                                            ...
                                                        </span>
                                                    );
                                                }

                                                if (totalPages > 1) {
                                                    pages.push(
                                                        <button
                                                            key={totalPages}
                                                            onClick={() => setPage(totalPages)}
                                                            className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${
                                                                page === totalPages
                                                                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm'
                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            {totalPages}
                                                        </button>
                                                    );
                                                }

                                                return pages;
                                            })()}
                                        </div>

                                        <button
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            disabled={page >= totalPages}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                                        >
                                            <span className="hidden sm:inline">Next</span>
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>

                                        <button
                                            onClick={() => setPage(totalPages)}
                                            disabled={page >= totalPages}
                                            className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                            title="Last page"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Logs Data Table */}
                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <LoadingSpinner size="lg" />
                                <span className="text-sm font-semibold text-gray-500 mt-3">Retrieving system activity...</span>
                            </div>
                        ) : error ? (
                            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mb-3">
                                    <svg className="h-6 w-6 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h4 className="text-base font-bold text-gray-900">Failed to Load Logs</h4>
                                <p className="text-sm text-gray-500 mt-1 max-w-md">{error}</p>
                                <button
                                    onClick={fetchAccessLogs}
                                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-all shadow-sm"
                                >
                                    Retry Fetch
                                </button>
                            </div>
                        ) : logs.length === 0 ? (
                            <div className="text-center py-20 px-4">
                                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 8.293A1 1 0 013 7.586V4z" />
                                    </svg>
                                </div>
                                <h4 className="text-base font-bold text-gray-900">No Logs Found</h4>
                                <p className="text-sm text-gray-500 mt-1">Try refining search parameters or adjusting date ranges.</p>
                            </div>
                        ) : (
                            <table className="min-w-max w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                                        <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator/Employee</th>
                                        <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                        <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                                        <th className="px-4 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {logs.map((log) => {
                                        const actorName = log.userId?.name || 'System / Unauthenticated';
                                        const userMail = log.userEmail || '—';
                                        
                                        return (
                                            <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                                                {/* Timestamp */}
                                                <td className="px-3 py-1 whitespace-nowrap text-xs font-medium text-gray-500">
                                                    {formatTimestamp(log.createdAt)}
                                                </td>

                                                {/* Operator/Employee */}
                                                <td className="px-3 py-1 whitespace-nowrap">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-[9px] font-semibold text-indigo-700 flex-shrink-0">
                                                            {getInitials(actorName)}
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-semibold text-gray-900 leading-tight">
                                                                {actorName}
                                                            </span>
                                                            <span className="text-[10px] text-gray-500 leading-none">
                                                                {userMail}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Action Code */}
                                                <td className="px-3 py-1 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${getActionBadgeStyle(log.action)}`}>
                                                        {log.action}
                                                    </span>
                                                </td>

                                                {/* Description */}
                                                <td className="px-3 py-1 max-w-xs truncate text-xs text-gray-600" title={log.description}>
                                                    {log.description}
                                                </td>

                                                {/* IP Address */}
                                                <td className="px-3 py-1 text-xs font-medium text-gray-600 whitespace-nowrap font-mono">
                                                    {log.ipAddress || '—'}
                                                </td>

                                                {/* Actions */}
                                                <td className="px-3 py-1 text-right whitespace-nowrap">
                                                    <button
                                                        onClick={() => setSelectedLog(log)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-[10px] font-semibold rounded shadow-sm hover:shadow transition-all"
                                                    >
                                                        Show
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Access History Sliding Side Drawer - Perfectly aligned with UserPermissionsReport */}
            {selectedLog && (
                <div className="fixed inset-0 overflow-hidden z-50">
                    <div className="absolute inset-0 overflow-hidden">
                        {/* Backdrop */}
                        <div 
                            className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
                            onClick={() => setSelectedLog(null)}
                        />

                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <div className="pointer-events-auto w-screen max-w-2xl transform transition-transform duration-300 ease-in-out">
                                <div className="flex h-full flex-col bg-white shadow-2xl border-l border-gray-200">
                                    
                                    {/* Drawer Header */}
                                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                                        <div>
                                            <h2 className="text-lg font-bold text-white">
                                                Access Event Audit Details
                                            </h2>
                                            <p className="text-xs text-white/80 mt-0.5">
                                                ID: {selectedLog._id} | Action: {selectedLog.action}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setSelectedLog(null)}
                                            className="rounded-md text-white/80 hover:text-white hover:bg-white/10 p-1.5 transition-all"
                                        >
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Drawer Body */}
                                    <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
                                        {/* Warning Banner if Failed Login */}
                                        {selectedLog.action === 'LOGIN_FAILED' && (
                                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs flex gap-2 items-start shadow-xs">
                                                <svg className="w-4 h-4 text-red-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <div>
                                                    <p className="font-bold">Security Alert: Failed Login Attempt</p>
                                                    <p className="mt-0.5 opacity-90">An unsuccessful authentication attempt was recorded. Please check the IP address and operator logs to ensure security compliance.</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Grid Details Card */}
                                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 p-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Operator Profile
                                                    </span>
                                                    <p className="text-sm font-semibold text-gray-900 mt-1">
                                                        {selectedLog.userId?.name || 'System / Unauthenticated'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Operator Email
                                                    </span>
                                                    <p className="text-sm font-medium text-gray-900 mt-1 truncate">
                                                        {selectedLog.userEmail || '—'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        Executed At
                                                    </span>
                                                    <p className="text-sm font-semibold text-gray-900 mt-1">
                                                        {formatTimestamp(selectedLog.createdAt)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                                                        IP Address
                                                    </span>
                                                    <div className="mt-1 flex items-center gap-1 font-mono text-sm text-gray-900">
                                                        <MapPinIcon className="h-4 w-4 text-indigo-500 shrink-0" />
                                                        {selectedLog.ipAddress || '—'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Description Details */}
                                        <div className="space-y-2">
                                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                Activity Summary
                                            </h3>
                                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-gray-950 font-medium leading-relaxed">
                                                {selectedLog.description}
                                            </div>
                                        </div>

                                        {/* Browser and Operating System details */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                                    <ComputerDesktopIcon className="h-3.5 w-3.5 text-indigo-500" />
                                                    Platform & Browser
                                                </h3>
                                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 font-semibold text-gray-800 text-xs">
                                                    {parseUserAgent(selectedLog.userAgent).browser} ({parseUserAgent(selectedLog.userAgent).os})
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                                    <ClockIcon className="h-3.5 w-3.5 text-indigo-500" />
                                                    HTTP Method & Status
                                                </h3>
                                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 font-semibold text-gray-800 text-xs flex items-center justify-between">
                                                    <span>Method: <span className="text-indigo-600 font-bold font-mono">{selectedLog.method || 'N/A'}</span></span>
                                                    <span>Status: <span className={`font-bold font-mono ${selectedLog.statusCode >= 400 ? 'text-rose-600' : 'text-green-600'}`}>{selectedLog.statusCode || 'N/A'}</span></span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Routing Resource Details */}
                                        {selectedLog.resource && (
                                            <div className="space-y-2">
                                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                    Request Path Resource
                                                </h3>
                                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 font-mono text-xs text-gray-700 break-all">
                                                    {selectedLog.resource}
                                                </div>
                                            </div>
                                        )}

                                        {/* System Permissions Details Section */}
                                        {(auditPermissions.length > 0 || auditPermissionsLoading) && (
                                            <div className="space-y-2 border-t border-gray-200 pt-4">
                                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                    {selectedLog.action === 'PERMISSIONS_UPDATED' && selectedLog.details?.permissions
                                                        ? `${selectedLog.details.updateType === 'revoke' ? 'Revoked' : 'Granted'} Permissions (${auditPermissions.length})`
                                                        : ['ROLE_ASSIGNED', 'ROLE_CREATED', 'ROLE_UPDATED', 'PERMISSIONS_UPDATED'].includes(selectedLog.action)
                                                            ? `Role Permissions (${auditPermissions.length})` 
                                                            : `User Active Permissions (${auditPermissions.length})`}
                                                </h3>
                                                {auditPermissionsLoading ? (
                                                    <div className="py-4 flex items-center justify-center">
                                                        <LoadingSpinner size="sm" />
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-1">
                                                        {[...auditPermissions]
                                                            .sort((a, b) => {
                                                                const modA = (a.module || 'SYSTEM').toUpperCase();
                                                                const modB = (b.module || 'SYSTEM').toUpperCase();
                                                                if (modA !== modB) return modA.localeCompare(modB);
                                                                return (a.name || '').localeCompare(b.name || '');
                                                            })
                                                            .map(p => (
                                                                <div 
                                                                    key={p.code} 
                                                                    className={`p-2 rounded-lg flex flex-col justify-between border ${
                                                                        p.changeType === 'revoked'
                                                                            ? 'bg-rose-50/40 border-rose-200'
                                                                            : p.changeType === 'granted'
                                                                                ? 'bg-emerald-50/40 border-emerald-200'
                                                                                : 'bg-gray-50 border-gray-200'
                                                                    }`}
                                                                >
                                                                    <div className="flex items-start justify-between gap-1.5">
                                                                        <span className="text-[11px] font-semibold text-gray-900 truncate" title={p.name}>
                                                                            {p.name || p.code}
                                                                        </span>
                                                                        <div className="flex items-center gap-1 shrink-0">
                                                                            {p.changeType && (
                                                                                <span className={`inline-block px-1 py-0.2 text-[7px] font-extrabold rounded uppercase ${
                                                                                    p.changeType === 'revoked'
                                                                                        ? 'bg-rose-100 text-rose-800 border border-rose-200'
                                                                                        : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                                                                }`}>
                                                                                    {p.changeType}
                                                                                </span>
                                                                            )}
                                                                            <span className="inline-block px-1.5 py-0.2 bg-indigo-50 text-indigo-700 text-[8px] font-bold rounded border border-indigo-200 uppercase">
                                                                                {p.module || 'SYSTEM'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <span className="text-[9px] font-mono text-gray-400 mt-1 truncate">
                                                                        {p.code}
                                                                    </span>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Details / Metadata Payload */}
                                        {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                        Event Metadata Payload
                                                    </h3>
                                                    <button
                                                        onClick={() => handleCopyToClipboard(selectedLog.details)}
                                                        className="flex items-center gap-1 px-2.5 py-1 border border-gray-300 rounded bg-white text-xs font-semibold text-gray-600 hover:bg-gray-50 shadow-xs transition-all"
                                                    >
                                                        {copied ? (
                                                            <>
                                                                <CheckIcon className="h-3.5 w-3.5 text-green-600" />
                                                                Copied!
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ClipboardIcon className="h-3.5 w-3.5" />
                                                                Copy Payload
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="bg-gray-950 rounded-lg p-4 border border-gray-800 text-gray-100 overflow-x-auto max-h-[300px]">
                                                    <pre className="font-mono text-xs leading-relaxed">
                                                        {JSON.stringify(selectedLog.details, null, 2)}
                                                    </pre>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Drawer Footer */}
                                    <div className="border-t border-gray-200 px-6 py-4 flex justify-end bg-gray-50">
                                        <button
                                            onClick={() => setSelectedLog(null)}
                                            className="px-4 py-2 border border-gray-300 rounded-md text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-xs"
                                        >
                                            Close Audit
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccessHistoryViewer;
